import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';

@Injectable({
    providedIn: 'root'
})
export class RegionStorageService {
    private readonly REGION_STORAGE_KEY = 'selectedRegionIds';
    private readonly COUNTRY_STORAGE_KEY = 'selectedCountryIds';

    /**
     * Save region IDs to localStorage.
     * Empty array is persisted so a cleared filter is not replaced by a default.
     */
    saveRegionIds(regionIds: number[]): void {
        localStorage.setItem(
            this.REGION_STORAGE_KEY,
            JSON.stringify(Array.isArray(regionIds) ? regionIds : [])
        );
    }

    /**
     * Get region IDs from localStorage.
     * Returns [] when unset/cleared (no forced Australia/ANZ default).
     */
    getRegionIds(): number[] {
        try {
            const savedRegionIds = localStorage.getItem(this.REGION_STORAGE_KEY);
            if (savedRegionIds == null) {
                return [];
            }
            const parsed = JSON.parse(savedRegionIds);
            return Array.isArray(parsed)
                ? parsed.map((id: any) => Number(id)).filter((id: number) => !!id)
                : [];
        } catch (error) {
            console.error('Error parsing saved region IDs:', error);
            return [];
        }
    }

    /**
     * Apply saved regions with fallback logic
     */
    applySavedRegions(
        regionControl: FormControl,
        regionList: any[],
        isCountryPic: boolean = false,
        onSuccess?: () => void
    ): boolean {
        const savedRegionIds = this.getRegionIds();

        if (savedRegionIds && regionList && regionList.length > 0 && !isCountryPic) {
            const validRegionObjects = savedRegionIds
                .map((id: number) => regionList.find((r: any) => r.id === id))
                .filter((region: any) => region !== undefined);

            if (validRegionObjects.length > 0) {
                // Set the region objects (not just IDs) to the FormControl
                regionControl.setValue(validRegionObjects);
                if (onSuccess) {
                    onSuccess();
                }
                return true;
            }
        }

        return false;
    }

    /**
     * Handle region selection change and save to localStorage
     */
    onRegionChange(regionControl: FormControl, regionList: any[], isCountryPic: boolean = false, onSuccess?: () => void): void {
        const selectedRegions = regionControl.value;

        if (selectedRegions && selectedRegions.length > 0) {
            const regionIds = selectedRegions.map((region: any) =>
                region?.id != null ? Number(region.id) : Number(region)
            ).filter((id: number) => !!id);
            this.saveRegionIds(regionIds);
        } else {
            // Allow empty selection (all regions) — do not force SEA/Australia back.
            this.saveRegionIds([]);
        }

        onSuccess?.();
    }

    /**
     * Check and apply stored regions without fallback logic
     */
    checkAndApplyStoredRegions(
        regionControl: FormControl,
        regionList: any[],
        isCountryPic: boolean = false,
        onSuccess?: () => void
    ): boolean {
        const savedRegionIds = this.getRegionIds();

        if (savedRegionIds && regionList && regionList.length > 0) {
            const validRegionObjects = savedRegionIds
                .map((id: number) => regionList.find((r: any) => r.id === id))
                .filter((region: any) => region !== undefined);

            if (validRegionObjects.length > 0 && (!regionControl.value || regionControl.value.length === 0)) {
                regionControl.setValue(validRegionObjects);
                onSuccess?.();
                return true;
            }
        }
        return false;
    }

    /**
     * Force region selection from storage (updates even if regions already selected)
     */
    forceRegionSelectionFromStorage(
        regionControl: FormControl,
        regionList: any[],
        isCountryPic: boolean = false,
        onSuccess?: () => void
    ): boolean {
        const savedRegionIds = this.getRegionIds();

        if (savedRegionIds && regionList && regionList.length > 0) {
            const validRegionObjects = savedRegionIds
                .map((id: number) => regionList.find((r: any) => r.id === id))
                .filter((region: any) => region !== undefined);

            if (validRegionObjects.length > 0) {
                // Check if current selection differs from stored selection
                const currentRegionIds = (regionControl.value || []).map((r: any) => r.id).sort();
                const savedRegionIdsSorted = savedRegionIds.sort();

                if (JSON.stringify(currentRegionIds) !== JSON.stringify(savedRegionIdsSorted)) {
                    regionControl.setValue(validRegionObjects);
                    onSuccess?.();
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Get region names by IDs (you'll need to inject MasterserviceService)
     */
    async getRegionNames(regionIds: number[]): Promise<string[]> {
        // You'll need to inject MasterserviceService to get region details
        // This is a placeholder - implement based on your service structure
        return regionIds.map(id => `Region ${id}`);
    }

    /**
     * Check and apply stored regions specifically for summary components
     */
    checkStoredRegionsForSummary(filterService: any): boolean {
        const savedRegionIds = this.getRegionIds();

        if (savedRegionIds && savedRegionIds.length > 0) {
            const currentFilters = filterService.getCurrentFilters();

            // Only update if current region filter is different from saved
            if (!currentFilters.regionId ||
                JSON.stringify(currentFilters.regionId) !== JSON.stringify(savedRegionIds)) {

                // For summary components, we just need to trigger the filter update
                // The region names can be fetched separately if needed
                filterService.updateRegionFilter(savedRegionIds, savedRegionIds.map((id: number) => `Region ${id}`));
                return true;
            }
        }

        return false;
    }

    // Country methods (new)
    saveCountryIds(countryIds: number[]): void {
        localStorage.setItem(
            this.COUNTRY_STORAGE_KEY,
            JSON.stringify(Array.isArray(countryIds) ? countryIds : [])
        );
    }

    getCountryIds(): number[] {
        try {
            const savedCountryIds = localStorage.getItem(this.COUNTRY_STORAGE_KEY);
            if (savedCountryIds == null) {
                return [];
            }
            const parsed = JSON.parse(savedCountryIds);
            return Array.isArray(parsed)
                ? parsed.map((id: any) => Number(id)).filter((id: number) => !!id)
                : [];
        } catch (error) {
            console.error('Error parsing saved country IDs:', error);
            return [];
        }
    }

    applySavedCountries(
        countryControl: FormControl,
        countryList: any[],
        onSuccess?: () => void
    ): boolean {
        const savedCountryIds = this.getCountryIds();

        if (savedCountryIds && countryList && countryList.length > 0) {
            const validCountryObjects = savedCountryIds
                .map((id: number) => countryList.find((c: any) => c.id === id))
                .filter((country: any) => country !== undefined);

            if (validCountryObjects.length > 0) {
                countryControl.setValue(validCountryObjects);
                if (onSuccess) {
                    onSuccess();
                }
                return true;
            }
        }
        return false;
    }

    /**
     * Remove country IDs that don't belong to the given regions
     */
    filterCountriesByRegions(regionIds: number[], masterService: any): Promise<void> {
        return new Promise((resolve) => {
            const savedCountryIds = this.getCountryIds();

            if (!savedCountryIds || savedCountryIds.length === 0 || !regionIds || regionIds.length === 0) {
                this.saveCountryIds([]);
                resolve();
                return;
            }

            // Get countries for the selected regions
            masterService.getCountryFilter(regionIds).subscribe((res: any) => {
                const regionCountries = res?.result || [];
                const regionCountryIds = regionCountries.map((c: any) => c.id);

                // Filter saved countries to only keep those that belong to the selected regions
                const validCountryIds = savedCountryIds.filter((countryId: number) =>
                    regionCountryIds.includes(countryId)
                );

                this.saveCountryIds(validCountryIds);
                resolve();
            });
        });
    }
} 